import { SPGameState } from "../enum/GameState";
import { SoundByte } from "../enum/sounds";
import { Input, KeyCode } from "../lib/Input";
import MonoBehavior from "../lib/MonoBehavior";
import { Time } from "../lib/Time";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import Color from "../util/Color";
import Listener from "../util/Listener";
import { Random } from "../util/Random";
import Explosion from "./Explosion";
import SoundManager from "./SoundManager";
import SPDash from "./SPAbilities/SPDash";
import SPAbility from "./SPAbility";
import SPBogey from "./SPBogey";
import SPBullet from "./SPBullet";
import SPGameManager from "./SPGameManager";
import SPGun from "./SPGun";
import SPScanner from "./SPScanner";
import SPTrail from "./SPTrail";

export default class SPPlayer extends MonoBehavior {
    private invulnTimer = 0;
    get isInvuln() {return this.invulnTimer > 0;}
    
    SetInvuln(time: number) {
        this.invulnTimer = time;
    }
    
    godMode = false;
    velocity: Vector2 = Vector2.zero;
    acceleration: Vector2 = Vector2.zero;
    friction: number = 0.5;
    accelerationForce: number = 128;
    size: number = 8;
    trail: SPTrail | undefined;
    cameraLocked = true;
    planeControls = false;

    gun = new SPGun(this, {
        fireRate: 2,
        magSize: 30,
        reloadTime: 1,
        mouseTrigger: 0,
        damage: 1,
        bulletSpeed: 200,
        maxEnemiesHit: 1
    });

    secondaryGun: SPGun|undefined;
    critChance = 0;

    hp = 3;
    maxHP = 3;

    level = 1;
    exp = 0;
    totalExp = 0;

    expTable = [
        15, 16, 19, 24, 31, 40, 51, 64, 79, 96, 115, 136, 159, 184, 211, 240, 271,
        304, 339, 376, 415, 456, 499, 544, 591, 640, 691, 744, 799, 856, 915, 976,
        1039, 1104, 1171, 1240, 1311, 1384, 1459, 1536, 1615, 1696, 1779, 1864,
        1951, 2040, 2131, 2224, 2319, 2416, 2515, 2616, 2719, 2824, 2931, 3040,
        3151, 3264, 3379, 3496, 3615, 3736, 3859, 3984, 4111, 4240, 4371, 4504,
        4639, 4776, 4915, 5056, 5199, 5344, 5491, 5640, 5791, 5944, 6099, 6256,
        6415, 6576, 6739, 6904, 7071, 7240, 7411, 7584, 7759, 7936, 8115, 8296,
        8479, 8664, 8851, 9040, 9231, 9424, 9619, 9816, 10015, 10216, 10419, 10624,
        10831, 11040, 11251, 11464, 11679, 11896, 12115, 12336, 12559, 12784, 13011,
        13240, 13471, 13704, 13939, 14176, 14415, 14656, 14899, 15144, 15391, 15640,
        15891, 16144, 16399, 16656, 16915, 17176, 17439, 17704, 17971, 18240, 18511,
        18784, 19059, 19336, 19615, 19896, 20179, 20464, 20751, 21040, 21331, 21624,
        21919, 22216, 22515, 22816, 23119, 23424, 23731, 24040, 24351, 24664, 24979,
        25296, 25615, 25936, 26259, 26584, 26911, 27240, 27571, 27904, 28239, 28576,
        28915, 29256, 29599, 29944, 30291, 30640, 30991, 31344, 31699, 32056, 32415,
        32776, 33139, 33504, 33871, 34240, 34611, 34984, 35359, 35736, 36115, 36496,
        36879, 37264, 37651, 38040, 38431, 38824, 39219, 39616,
    ];

    maxLevel = 30;

    crosshairSize = 4;

    events = new Listener();

    getTotalXpForLevel(level: number) {
        return this.expTable.slice(0, level - 1).reduce((a,b) => a+b);
    }

    get xpToNextLevel() {
        if (this.level >= this.maxLevel) return 0;
        return this.expTable[this.level - 1];
    }

    get isDead() {
        return this.hp < 1;
    }

    abilities: SPAbility[] = [];
    triggers = [
        KeyCode.Digit1,
        KeyCode.Digit2,
        KeyCode.Digit3,
        KeyCode.Digit4,
        KeyCode.Digit5,
        KeyCode.Digit6,
        KeyCode.Digit7,
        KeyCode.Digit8,
        KeyCode.Digit9,
        KeyCode.Digit0,
    ];

    AddAbility(ability: SPAbility) {
        if (this.abilities.length === this.triggers.length) return;
        if (ability instanceof SPDash) ability.trigger = KeyCode.Space;
        else ability.trigger = this.triggers[this.abilities.length];
        this.abilities.push(ability);
    }

    GetAbility(T: typeof SPAbility) {
        for (let i = 0; i < this.abilities.length; i++) {
            if (this.abilities[i] instanceof T) {
                return this.abilities[i];
            }
        }
    }

    RemoveAbility(T: typeof SPAbility) {
        for (let i = this.abilities.length - 1; i >= 0; i--) {
            if (this.abilities[i] instanceof T) {
                this.abilities.splice(i, 1);
            }
        }
    }

    AddExp(amt: number) {
        if (this.level >= this.maxLevel) return;

        this.exp += amt;
        this.totalExp += amt;

        while (this.exp >= this.xpToNextLevel) {
            this.LevelUp();

            if (this.level >= this.maxLevel) break;
        }
    }

    SubExp(amt: number) {
        if (this.level === 1 && this.exp === 0) return;

        this.exp -= amt;
        this.totalExp -= amt;

        if (this.exp < 0) {
            if (this.level > 1) {
                this.level--;
                this.exp = this.xpToNextLevel - 1;

                SPGameManager.Singleton.takenAbilities[this.level + 1]?.onLose();
            } else {
                this.exp = 0;
            }
        }
    }

    LevelUpExplosion() {
        this.engine.NewGameObjectWithScript<Explosion>(Explosion, {
            position: this.transform.position,
            initialValues: {
                size: 128,
                w: 256,
                color: new Color(255,255,80)
            }
        });

        for (const bogey of this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey)) {
            if (Physics.CircleCircleCollision({
                position: this.transform.position,
                radius: 128
            }, {
                position: bogey.transform.position,
                radius: bogey.size
            }) !== false) {
                bogey.Damage(1);
            }
        }
    }

    LevelUp() {
        if (this.level >= this.maxLevel) return;

        this.exp -= this.xpToNextLevel;
        this.level++;

        this.LevelUpExplosion();

        SPGameManager.Singleton.SetState(SPGameState.AbilityPick);

        if (this.level >= this.maxLevel) {
            this.exp = 0;
        }
    }

    Damage(amt: number, options: {
        bypassExp?: boolean;
        attacker?: SPBogey|SPPlayer;
    } = {}) {
        if (this.isDead || this.isInvuln || this.godMode) return;
        this.invulnTimer = 1;

        this.events.send("on-damage", {amt, options});

        if ((this.level === 1 && this.exp === 0) || options.bypassExp) {
            this.hp -= 1;
            this.invulnTimer = 1;
        } else {
            this.SubExp(this.exp+1);
        }

        if (this.isDead) this.Die();
    }

    static Singleton: SPPlayer;
    Init(): void {

        if (SPPlayer.Singleton == undefined) {
            SPPlayer.Singleton = this;
        } else if (!SPPlayer.Singleton.Equals(this)) {
            this.gameObject.RemoveScript(this.id);
            return;
        }

        this.trail = new SPTrail(this.gameObject);
        this.trail.color = new Color(80, 255, 80);

        this.gun.onShoot = () => {
            this.engine.NewGameObjectWithScript(SPBullet, {
                position: this.transform.position.add(
                    this.transform.forward.scale(this.size + 3)
                ),
                initialValues: {
                    velocity: this.velocity.add(
                        this.transform.forward.scale(this.gun.bulletSpeed)
                    ),
                    damage: this.gun.damage,
                },
            });
        };
    }

    Die() {
        this.hp = 0;
        this.engine.NewGameObjectWithScript(Explosion, {
            position: this.transform.position,
            initialValues: {
                size: this.size * 2,
                w: this.size * 4,
                color: this.trail?.color ?? new Color(255, 255, 255),
            },
        });
        SoundManager.Singleton.PlaySound(SoundByte.Explosion);

        this.acceleration = Vector2.zero;
        this.velocity = Vector2.zero;
        SPGameManager.Singleton.SetState(SPGameState.GameOver);
    }

    Update(): void {
        if (this.isDead || SPGameManager.Singleton.state !== SPGameState.InGame) return;

        if (this.isInvuln) {
            this.invulnTimer -= Time.deltaTime;

            if (this.invulnTimer <= 0)  this.invulnTimer = 0;
        }

        this.gun.Update();
        this.secondaryGun?.Update();

        for (const ability of this.abilities) {
            ability.Update();
        }

        const input = this.planeControls ? this.transform.forward.clone() : new Vector2(
            Input.GetKey(KeyCode.KeyD) ? 1 : Input.GetKey(KeyCode.KeyA) ? -1 : 0,
            Input.GetKey(KeyCode.KeyS) ? 1 : Input.GetKey(KeyCode.KeyW) ? -1 : 0
        );

        if (input.squareMagnitude > 0) input.normalize();

        this.acceleration = input.scale(this.accelerationForce);

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(
            this.velocity.scale(this.friction * Time.deltaTime)
        );
        this.transform.position = this.transform.position.add(
            this.velocity.scale(Time.deltaTime)
        );
    }

    LateUpdate(): void {
        if (SPGameManager.Singleton.state !== SPGameState.InGame) return;

        const desiredRotation = this.engine.mouseWorldPosition.sub(
            this.transform.position
        ).normalized;

        this.transform.rotation = this.planeControls ? Vector2.lerp(this.transform.rotation, desiredRotation, Math.PI * Time.deltaTime) : desiredRotation;

        if (!this.cameraLocked) {
            this.engine.camera = Vector2.lerp(
                this.engine.camera,
                this.transform.position,
                Time.deltaTime * 16
            );
        } else {
            // this.engine.camera = Vector2.lerp(this.engine.camera, this.transform.position, Time.deltaTime * 16);
            if (
                this.transform.position.x + this.size >
                this.engine.camera.x + this.engine.width / 2
            ) {
                this.transform.position.x =
                    this.engine.camera.x + this.engine.width / 2 - this.size;
                this.velocity.x = 0;
            }
            if (
                this.transform.position.x - this.size <
                this.engine.camera.x - this.engine.width / 2
            ) {
                this.transform.position.x =
                    this.engine.camera.x - this.engine.width / 2 + this.size;
                this.velocity.x = 0;
            }
            if (
                this.transform.position.y + this.size >
                this.engine.camera.y + (this.engine.height/2 - 40)
            ) {
                this.transform.position.y =
                    this.engine.camera.y + this.engine.height / 2 - 40 - this.size;
                this.velocity.y = 0;
            }
            if (
                this.transform.position.y - this.size <
                this.engine.camera.y - this.engine.height / 2
            ) {
                this.transform.position.y =
                    this.engine.camera.y - this.engine.height / 2 + this.size;
                this.velocity.y = 0;
            }
        }
        this.trail?.LateUpdate();
    }

    DrawDeathScreen() {
        this.engine.drawTexts([
            `You died`,
            `Final score: ${this.totalExp.toLocaleString()}`
        ], this.engine.center, {
            baseline: 'middle',
            align: 'center',
            size: 24,
            color: 'white'
        });
    }

    Draw(): void {

        if (this.isDead) {
            this.DrawDeathScreen();
            return;
        }

        this.trail?.Draw();

        //player
        const p1 = this.transform.position.add(
            this.transform.forward.scale(this.size)
        );
        const p2 = this.transform.position.add(
            this.transform.right.rotate(Math.PI / 4).scale(this.size)
        );
        const p3 = this.transform.position.add(
            this.transform.back.scale(this.size / 2)
        );
        const p4 = this.transform.position.add(
            this.transform.left.rotate(-Math.PI / 4).scale(this.size)
        );
        this.ctx.fillStyle = "black";
        this.ctx.strokeStyle = "#55ff55";
        this.ctx.lineWidth = 1;
        this.engine.strokeShape(p1, p2, p3, p4);
        this.engine.fillShape(p1, p2, p3, p4);

        this.gun.Draw(4);
        this.secondaryGun?.Draw(10);
        //


        //#region HUD
        this.ctx.fillStyle = '#101010';
        this.ctx.fillRect(0, this.engine.height - 44, this.engine.width, 44);
        this.ctx.fillStyle = '#151515';
        this.ctx.fillRect(this.engine.width - 384, this.engine.height - 44, 384, 40);

        const hudDrawPos = new Vector2(0, this.engine.height - 4);
        //exp bar
        this.ctx.fillStyle = this.level >= this.maxLevel ? "#ffff55" : "#ffff5510";
        this.ctx.fillRect(hudDrawPos.x, hudDrawPos.y, this.engine.width, 4);

        if (this.level < this.maxLevel) {
            this.ctx.fillStyle = "#ffff55";
            this.ctx.fillRect(
                hudDrawPos.x,
                hudDrawPos.y,
                this.engine.width * (this.exp / this.xpToNextLevel),
                4
            );

            //level and xp text
            this.engine.drawTexts([
                `Lv.${this.level}`,
                // `${this.exp.toLocaleString()}/${this.xpToNextLevel.toLocaleString()}XP`
            ], hudDrawPos.add(new Vector2(4, -18)), {
                align: 'left',
                baseline: 'middle',
                size: 16
            });
            this.ctx.textAlign = 'left';
            this.ctx.textBaseline = 'top';
        }
        //

        //ability slots
        this.ctx.fillStyle = '#00000055';
        for (let i = 0; i < 10; i++) {
            const x = this.engine.width - 360 + 38*i - 4;
            this.engine.FillRectScreenFromCenter(new Vector2(x, this.engine.height-24), new Vector2(32,32));
        }
        //

        //abilities
        for (let i = 0; i < this.abilities.length; i++) {
            const ability = this.abilities[i];
            ability.Draw(i);
        }
        //

        //Player lives
        const lifeDrawPos = new Vector2(48, this.engine.height - 22);

        this.engine.drawTexts(['Lives'], lifeDrawPos.add(new Vector2(-4, -8)), {
            align: 'left',
            baseline: 'bottom',
            color: 'white'
        });

        for (let i = 1; i <= this.maxHP; i++) {
            const p1 = lifeDrawPos.add(Vector2.up.scale(8));
            const p2 = lifeDrawPos.add(Vector2.right.rotate(Math.PI/4).scale(8));
            const p3 = lifeDrawPos.add(Vector2.down.scale(4));
            const p4 = lifeDrawPos.add(Vector2.left.rotate(-Math.PI/4).scale(8));
            this.ctx.strokeStyle = this.hp >= i ? '#55ff55' : '#404040';
            this.ctx.lineWidth = 1;
            this.engine.strokeShapeScreenPos(p1,p2,p3,p4);
            lifeDrawPos.x += 16;
            // this.ctx.fillStyle = this.hp > i ? '#55ff55' : '#404040';
            // this.engine.FillCircleScreen(lifeDrawPos.add(new Vector2(12 * i, 0)), 4);
        }
        //
        //#endregion
    }

    DrawGizmos(): void {
        const textpos = this.transform.position.add(Vector2.one.scale(this.size + 4));
        this.engine.drawTextsWorldPosition([
            `${Math.floor(this.transform.position.x)} ${Math.floor(this.transform.position.y)}`,
            `V: ${Math.floor(this.velocity.magnitude)} u/s`,
            `A: ${Math.floor(this.acceleration.magnitude)} u/s`,
            `Invlun: ${this.isInvuln}`,
            `Score: ${this.totalExp.toLocaleString()}`,
            `DPS: ${this.gun.fireRate * this.gun.damage}`
        ], textpos, {
            color: this.engine.viewport.fColor
        });
    }
}
